"use client";

import { IconShieldPlus, IconShieldSlash } from "@decibeltrade/react-icons";
import { insetFocusRing } from "@decibeltrade/web-core/recipes";
import { useTranslations } from "next-intl";
import { css, cva, cx } from "styled-system/css";
import { flex } from "styled-system/patterns";

import { openEncryptionModal } from "@/components/modal/encryption-modal";
import { useAnalytics } from "@/contexts/analytics-context";
import { useWriteDexSdk } from "@/contexts/write-dex-sdk-context";
import { useEncryptionCohortState } from "@/hooks/use-encryption-policy";

import { Clickable } from "./clickable";

export interface EncryptionIndicatorProps {
  className?: string;
  /** Runs before the modal opens, e.g. to dismiss a drawer the indicator sits in. */
  onOpen?: () => void;
}

export function EncryptionIndicator({ className, onOpen }: EncryptionIndicatorProps = {}) {
  const t = useTranslations("encryption");
  const analytics = useAnalytics();
  const cohortState = useEncryptionCohortState();
  const { writesEncrypted } = useWriteDexSdk();

  if (cohortState === "hidden") return null;

  // The cohort decides whether this device encrypts at all; the write path
  // decides whether the next submission actually will. Both must agree before we
  // claim protection the user isn't getting.
  const isEncrypted = cohortState === "encrypted" && writesEncrypted;
  const state = isEncrypted ? "encrypted" : "paused";
  const Icon = isEncrypted ? IconShieldPlus : IconShieldSlash;

  return (
    <Clickable
      type="button"
      ariaLabel={t("indicatorAriaLabel")}
      data-testid="encryption-indicator"
      onClick={() => {
        onOpen?.();
        openEncryptionModal({ state }, analytics);
      }}
      className={cx(
        flex({ alignItems: "center", gap: "4" }),
        indicatorStyles({ tone: isEncrypted ? "encrypted" : "paused" }),
        insetFocusRing(),
        className,
      )}
    >
      <p className={css({ textStyle: "body.md", mb: "-4" })}>
        {isEncrypted ? t("encrypted") : t("paused")}
      </p>
      <Icon className={css({ w: "16", h: "16" })} />
    </Clickable>
  );
}

const indicatorStyles = cva({
  base: { transitionPreset: "colors" },
  variants: {
    tone: {
      encrypted: { color: "text.positive" },
      paused: { color: "text.negative" },
    },
  },
});
